/**
 * Zone 3: Impact & Related Signals
 *
 * Shows cascading effects, related entities, and patterns.
 * NOT a full causal history — focused on what matters NOW.
 */

import type { ImpactSignal } from '../../services/investigationEngine';

interface ImpactSignalsProps {
  signals: readonly ImpactSignal[];
  onNavigate?: (entityType: string, entityId: string) => void;
}

const SEVERITY_STYLES: Record<string, { dot: string; bg: string }> = {
  critical: { dot: 'bg-rose-400', bg: 'hover:bg-rose-950/20' },
  warning: { dot: 'bg-amber-400', bg: 'hover:bg-amber-950/20' },
  info: { dot: 'bg-slate-400', bg: 'hover:bg-slate-700/30' },
};

const ENTITY_ICON: Record<string, string> = {
  room: '🚪',
  guest: '👤',
  staff: '👷',
  floor: '🏢',
};

export function ImpactSignals({ signals, onNavigate }: ImpactSignalsProps) {
  if (signals.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg px-5 py-6 text-center">
        <p className="text-sm text-slate-500">No related impact detected</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-700/50">
        <h3 className="text-xs font-semibold text-slate-300 uppercase tracking-wider">
          Impact & related signals
        </h3>
      </div>

      <div className="divide-y divide-slate-700/30">
        {signals.map((signal) => {
          const style = SEVERITY_STYLES[signal.severity] ?? SEVERITY_STYLES.info!;
          const canNavigate = signal.entityType === 'room' || signal.entityType === 'guest';

          return (
            <button
              key={signal.id}
              type="button"
              disabled={!canNavigate}
              onClick={() => canNavigate && onNavigate?.(signal.entityType, signal.entityId)}
              className={`w-full px-5 py-3 flex items-start gap-3 text-left transition-colors ${style.bg} ${
                canNavigate ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              <span className="text-sm mt-0.5 flex-shrink-0">
                {ENTITY_ICON[signal.entityType] ?? '•'}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${style.dot}`} />
                  <span className="text-xs font-medium text-slate-200">{signal.label}</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-0.5">{signal.description}</p>
              </div>
              {canNavigate && (
                <svg className="w-3.5 h-3.5 text-slate-600 flex-shrink-0 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
